import { Box, Button, Typography } from '@mui/material'
import { forwardRef } from 'react'
import { TextInputField, TextInputFieldProps } from 'utoniq-ui'

import { tLabel, useMessage, useTranslation } from '@/core'
import { Cell, CellFrame, Organism } from '@/core/architecture'
import { Form, useForm, UseFormProps } from '@/core/form'

export type MailAuthFormProps = {
  loading: boolean
  onSubmitMailAuth: (inputs: MailAuthFormValues) => Promise<void>
  onResendAuthMail: () => Promise<void>
}

export type MailAuthFormValues = {
  code: string
}

const schema: UseFormProps['schema'] = o => ({
  code: o.string().length(6).label(tLabel('mailAuth.code')),
})

export const MailAuthForm: Organism<MailAuthFormProps> = ({
  loading,
  onSubmitMailAuth,
  onResendAuthMail,
}) => {
  const { t } = useTranslation('mailAuth')
  const { showMessage, showError } = useMessage()

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm<MailAuthFormValues>({
    schema,
  })

  const onVerify = handleSubmit(onSubmitMailAuth)

  const onReSend = () =>
    onResendAuthMail()
      .then(() => showMessage(t('resended')))
      .catch(e => showError(e))

  return (
    <Form onSubmit={onVerify}>
      <TextInputCell type="text" error={errors?.code} {...register('code')} />
      <ButtonCell loading={isSubmitting} />
      <ResendButtonCell onResendAuthMail={onReSend} loading={loading} />
    </Form>
  )
}

const TextInputCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('mailAuth')

    return (
      <CellFrame>
        <TextInputField
          {...props}
          ref={ref}
          t={t}
          titleProps={{ sx: { display: 'none' } }}
        />
      </CellFrame>
    )
  }
)

type ButtonCellProps = {
  loading: boolean
}
const ButtonCell: Cell<ButtonCellProps> = ({ loading }) => {
  const { t } = useTranslation('mailAuth')

  return (
    <CellFrame sx={{ py: 2, textAlign: 'center' }}>
      <Button type="submit" size="large" sx={{ width: 244 }} loading={loading}>
        {t('submit')}
      </Button>
    </CellFrame>
  )
}

const ResendButtonCell: Cell<{
  onResendAuthMail: () => void
  loading: boolean
}> = ({ onResendAuthMail, loading }) => {
  const { t } = useTranslation('mailAuth')

  return (
    <CellFrame sx={{ py: 2 }}>
      <Box sx={{ textAlign: 'right' }}>
        <Button
          size="small"
          variant="text"
          onClick={onResendAuthMail}
          loading={loading}
        >
          <Typography variant="caption">{t('resend')}</Typography>
        </Button>
      </Box>
    </CellFrame>
  )
}
